/**
 * Form Version Utilities
 * Determines which TDS Return form version to use based on the selected tax year (financial year).
 * - Legacy forms: 26Q and 27Q (FY 2025-26 and earlier)
 * - New forms: 140 and 144 (FY 2026-27 onwards)
 */

export const FORM_VERSION_THRESHOLD_FY = '2026-27'

export const NEW_FORM_TYPES = {
  '26Q': '140',
  '27Q': '144',
}

export const LEGACY_FORM_TYPES = {
  '140': '26Q',
  '144': '27Q',
}

/**
 * Parse a financial year string (e.g., '2026-27') into a comparable numeric value.
 * Returns the start year as a number (e.g., 2026).
 */
const parseFYStartYear = (fy) => {
  if (!fy || typeof fy !== 'string') return 0
  const parts = fy.split('-')
  return parseInt(parts[0], 10) || 0
}

/**
 * Determines if the given financial year uses the new form version (140/144).
 * @param {string} financialYear - e.g., '2026-27', '2025-26'
 * @returns {boolean} true if new form version applies
 */
export const isNewFormVersion = (financialYear) => {
  const fyStart = parseFYStartYear(financialYear)
  const thresholdStart = parseFYStartYear(FORM_VERSION_THRESHOLD_FY)
  return fyStart >= thresholdStart
}

/**
 * Returns the form version identifier based on financial year.
 * @param {string} financialYear - e.g., '2026-27'
 * @returns {'legacy' | 'new'} form version
 */
export const getFormVersion = (financialYear) => {
  return isNewFormVersion(financialYear) ? 'new' : 'legacy'
}

/**
 * Maps the original form type to the new form type based on financial year.
 * For FY >= 2026-27: '26Q' → '140', '27Q' → '144'
 * For FY <= 2025-26: returns the original form type unchanged.
 * @param {string} financialYear - e.g., '2026-27'
 * @param {string} originalFormType - e.g., '26Q', '27Q'
 * @returns {string} effective form type
 */
export const getFormType = (financialYear, originalFormType) => {
  if (!isNewFormVersion(financialYear)) return originalFormType
  return NEW_FORM_TYPES[originalFormType] || originalFormType
}

/**
 * Gets the display label for a form type.
 * @param {string} formType - e.g., '140', '144', '26Q', '27Q'
 * @returns {string} display label
 */
export const getFormTypeLabel = (formType) => {
  const labels = {
    '26Q': '26Q',
    '27Q': '27Q',
    '140': '140 (Original)',
    '144': '144 (Original)',
  }
  return labels[formType] || formType
}
